import dayjs from "dayjs";

// Motor de correlação da Central de Alertas.
// Regras (spec):
// - Alerta <-> atendimento: mesmo equipamentoId E atendimento aberto até
//   30 min após a queda do alerta.
// - Atendimento <-> OS: pelo atendimentoId.
// - MTTA = minutos entre queda e abertura do atendimento correlacionado.
// - MTTR = minutos entre queda e recuperação (null se não recuperado).
// - SLA cumprido = MTTR <= contratoSla do cliente.
// Detecções inteligentes:
// - zona_morta: alerta sem atendimento correlacionado após 30 min.
// - os_faltante: atendimento resolvido sem OS vinculada.
// - falha_recorrente: >= 3 quedas no mesmo equipamento em janela de 24h.
// - sla_em_risco: incidente aberto cujo tempo desde a queda já passou de
//   70% do contratoSla.

export const JANELA_CORRELACAO_MINUTOS = 30;
export const JANELA_RECORRENCIA_HORAS = 24;
export const MINIMO_QUEDAS_RECORRENCIA = 3;
export const FRACAO_SLA_EM_RISCO = 0.7;

export function minutosEntre(inicioIso, fimIso) {
	if (!inicioIso || !fimIso) return null;
	return dayjs(fimIso).diff(dayjs(inicioIso), "minute", true);
}

function porId(lista) {
	const mapa = {};
	(lista || []).forEach((item) => {
		mapa[item.id] = item;
	});
	return mapa;
}

// Equipamentos com >= 3 quedas em qualquer janela deslizante de 24h.
function detectarEquipamentosRecorrentes(alertas) {
	const quedasPorEquipamento = {};
	(alertas || []).forEach((alerta) => {
		if (!quedasPorEquipamento[alerta.equipamentoId]) {
			quedasPorEquipamento[alerta.equipamentoId] = [];
		}
		quedasPorEquipamento[alerta.equipamentoId].push(alerta.dataHoraQueda);
	});

	const recorrentes = new Set();
	Object.entries(quedasPorEquipamento).forEach(([equipamentoId, quedas]) => {
		const ordenadas = quedas
			.slice()
			.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
		for (let i = 0; i < ordenadas.length; i++) {
			const fimJanela = dayjs(ordenadas[i]).add(JANELA_RECORRENCIA_HORAS, "hour");
			let contagem = 0;
			for (let j = i; j < ordenadas.length; j++) {
				if (dayjs(ordenadas[j]).isAfter(fimJanela)) break;
				contagem++;
			}
			if (contagem >= MINIMO_QUEDAS_RECORRENCIA) {
				recorrentes.add(equipamentoId);
				break;
			}
		}
	});
	return recorrentes;
}

// Status derivado do incidente (terminologia fixa):
// aberto -> reconhecido -> em_andamento -> resolvido -> fechado.
function derivarStatusIncidente(alerta, atendimento, os) {
	const recuperado = Boolean(alerta.dataHoraRecuperacao);
	if (recuperado) {
		const atendimentoEncerrado = !atendimento || atendimento.status === "resolvido";
		const osEncerrada = !os || os.status === "concluida" || os.status === "cancelada";
		return atendimentoEncerrado && osEncerrada ? "fechado" : "resolvido";
	}
	if (
		(atendimento && atendimento.status === "em_andamento") ||
		(os && os.status === "em_execucao")
	) {
		return "em_andamento";
	}
	if (alerta.reconhecido || atendimento) return "reconhecido";
	return "aberto";
}

// Correlaciona alertas, atendimentos e OS em incidentes.
// `agora` (ISO ou dayjs) é a referência para detecções dependentes do tempo.
export function correlacionarIncidentes({
	clientes,
	equipamentos,
	alertas,
	atendimentos,
	ordensServico,
	agora,
}) {
	const referencia = dayjs(agora);
	const clientesPorId = porId(clientes);
	const equipamentosPorId = porId(equipamentos);
	const osPorAtendimento = {};
	(ordensServico || []).forEach((os) => {
		osPorAtendimento[os.atendimentoId] = os;
	});

	const equipamentosRecorrentes = detectarEquipamentosRecorrentes(alertas);

	// Ordena alertas por queda para atribuição gulosa: cada atendimento
	// correlaciona com no máximo um alerta (o de queda mais próxima).
	const alertasOrdenados = (alertas || [])
		.slice()
		.sort(
			(a, b) => dayjs(a.dataHoraQueda).valueOf() - dayjs(b.dataHoraQueda).valueOf()
		);
	const atendimentosUsados = new Set();

	const incidentes = alertasOrdenados.map((alerta) => {
		const atendimento =
			(atendimentos || [])
				.filter((atd) => {
					if (atendimentosUsados.has(atd.id)) return false;
					if (atd.equipamentoId !== alerta.equipamentoId) return false;
					const delta = minutosEntre(alerta.dataHoraQueda, atd.dataHoraAbertura);
					return delta !== null && delta >= 0 && delta <= JANELA_CORRELACAO_MINUTOS;
				})
				.sort(
					(a, b) =>
						dayjs(a.dataHoraAbertura).valueOf() - dayjs(b.dataHoraAbertura).valueOf()
				)[0] || null;
		if (atendimento) atendimentosUsados.add(atendimento.id);

		const os = atendimento ? osPorAtendimento[atendimento.id] || null : null;

		const mtta = atendimento
			? minutosEntre(alerta.dataHoraQueda, atendimento.dataHoraAbertura)
			: null;
		const mttr = alerta.dataHoraRecuperacao
			? minutosEntre(alerta.dataHoraQueda, alerta.dataHoraRecuperacao)
			: null;

		const cliente = clientesPorId[alerta.clienteId] || null;
		const contratoSla = cliente ? cliente.contratoSla : null;
		const slaCumprido =
			mttr !== null && contratoSla !== null ? mttr <= contratoSla : false;

		const status = derivarStatusIncidente(alerta, atendimento, os);
		const incidenteAberto = !alerta.dataHoraRecuperacao;

		const categoriasInteligentes = [];
		// zona_morta: sem atendimento após 30 min da queda (limite: a
		// recuperação, se houver, senão o agora).
		const fimJanelaZonaMorta = alerta.dataHoraRecuperacao
			? dayjs(alerta.dataHoraRecuperacao)
			: referencia;
		const minutosSemAtendimento = fimJanelaZonaMorta.diff(
			dayjs(alerta.dataHoraQueda),
			"minute",
			true
		);
		if (!atendimento && minutosSemAtendimento > JANELA_CORRELACAO_MINUTOS) {
			categoriasInteligentes.push("zona_morta");
		}
		if (atendimento && atendimento.status === "resolvido" && !os) {
			categoriasInteligentes.push("os_faltante");
		}
		if (equipamentosRecorrentes.has(alerta.equipamentoId)) {
			categoriasInteligentes.push("falha_recorrente");
		}
		const minutosDesdeQueda = referencia.diff(
			dayjs(alerta.dataHoraQueda),
			"minute",
			true
		);
		if (
			incidenteAberto &&
			contratoSla !== null &&
			minutosDesdeQueda > contratoSla * FRACAO_SLA_EM_RISCO
		) {
			categoriasInteligentes.push("sla_em_risco");
		}

		return {
			id: `inc-${alerta.id}`,
			alerta,
			atendimento,
			os,
			recuperacao: alerta.dataHoraRecuperacao || null,
			mtta,
			mttr,
			slaCumprido,
			status,
			aberto: incidenteAberto,
			categoriasInteligentes,
			cliente,
			equipamento: equipamentosPorId[alerta.equipamentoId] || null,
			severidade: alerta.severidade,
			dataHoraQueda: alerta.dataHoraQueda,
		};
	});

	// Mais recentes primeiro.
	return incidentes.sort(
		(a, b) => dayjs(b.dataHoraQueda).valueOf() - dayjs(a.dataHoraQueda).valueOf()
	);
}

// Gera a lista unificada de EventoGlobal a partir das entidades brutas.
// Recebe os incidentes já correlacionados para vincular cada evento ao
// incidente correspondente (scroll da TimelineGlobal até a tabela).
export function gerarEventosGlobais({
	alertas,
	atendimentos,
	ordensServico,
	equipamentos,
	clientes,
	incidentes,
}) {
	const equipamentosPorId = porId(equipamentos);
	const clientesPorId = porId(clientes);
	const alertasPorId = porId(alertas);
	const atendimentosPorId = porId(atendimentos);

	const incidentePorAlerta = {};
	const incidentePorAtendimento = {};
	const incidentePorOs = {};
	(incidentes || []).forEach((incidente) => {
		incidentePorAlerta[incidente.alerta.id] = incidente.id;
		if (incidente.atendimento) {
			incidentePorAtendimento[incidente.atendimento.id] = incidente.id;
		}
		if (incidente.os) incidentePorOs[incidente.os.id] = incidente.id;
	});

	const nomeEquipamento = (id) => equipamentosPorId[id]?.nome || "—";
	const eventos = [];

	(alertas || []).forEach((alerta) => {
		eventos.push({
			id: `evt-queda-${alerta.id}`,
			tipo: "queda",
			dataHora: alerta.dataHoraQueda,
			clienteId: alerta.clienteId,
			equipamentoId: alerta.equipamentoId,
			severidade: alerta.severidade,
			referenciaId: alerta.id,
			incidenteId: incidentePorAlerta[alerta.id] || null,
			resumo: `Queda em ${nomeEquipamento(alerta.equipamentoId)}: ${alerta.titulo}`,
		});
		if (alerta.dataHoraRecuperacao) {
			eventos.push({
				id: `evt-recuperacao-${alerta.id}`,
				tipo: "recuperacao",
				dataHora: alerta.dataHoraRecuperacao,
				clienteId: alerta.clienteId,
				equipamentoId: alerta.equipamentoId,
				severidade: alerta.severidade,
				referenciaId: alerta.id,
				incidenteId: incidentePorAlerta[alerta.id] || null,
				resumo: `${nomeEquipamento(alerta.equipamentoId)} voltou a operar`,
			});
		}
	});

	(atendimentos || []).forEach((atendimento) => {
		const severidade = atendimento.alertaId
			? alertasPorId[atendimento.alertaId]?.severidade || null
			: null;
		eventos.push({
			id: `evt-atd-aberto-${atendimento.id}`,
			tipo: "atendimento_aberto",
			dataHora: atendimento.dataHoraAbertura,
			clienteId: atendimento.clienteId,
			equipamentoId: atendimento.equipamentoId,
			severidade,
			referenciaId: atendimento.id,
			incidenteId: incidentePorAtendimento[atendimento.id] || null,
			resumo: `${atendimento.titulo} — aberto por ${atendimento.abertoPor}`,
		});
		if (atendimento.dataHoraFechamento) {
			eventos.push({
				id: `evt-atd-fechado-${atendimento.id}`,
				tipo: "atendimento_fechado",
				dataHora: atendimento.dataHoraFechamento,
				clienteId: atendimento.clienteId,
				equipamentoId: atendimento.equipamentoId,
				severidade,
				referenciaId: atendimento.id,
				incidenteId: incidentePorAtendimento[atendimento.id] || null,
				resumo: `${atendimento.titulo} — encerrado`,
			});
		}
	});

	(ordensServico || []).forEach((os) => {
		const atendimento = atendimentosPorId[os.atendimentoId];
		const severidade = atendimento?.alertaId
			? alertasPorId[atendimento.alertaId]?.severidade || null
			: null;
		eventos.push({
			id: `evt-os-criada-${os.id}`,
			tipo: "os_criada",
			dataHora: os.dataHoraCriacao,
			clienteId: os.clienteId,
			equipamentoId: os.equipamentoId,
			severidade,
			referenciaId: os.id,
			incidenteId: incidentePorOs[os.id] || null,
			resumo: `${os.titulo} — técnico ${os.tecnico}`,
		});
		if (os.dataHoraConclusao) {
			eventos.push({
				id: `evt-os-concluida-${os.id}`,
				tipo: "os_concluida",
				dataHora: os.dataHoraConclusao,
				clienteId: os.clienteId,
				equipamentoId: os.equipamentoId,
				severidade,
				referenciaId: os.id,
				incidenteId: incidentePorOs[os.id] || null,
				resumo: `${os.titulo} — concluída por ${os.tecnico}`,
			});
		}
	});

	// Anexa nomes para exibição direta na timeline.
	eventos.forEach((evento) => {
		evento.clienteNome = clientesPorId[evento.clienteId]?.nome || "—";
		evento.equipamentoNome = nomeEquipamento(evento.equipamentoId);
	});

	// Mais recentes primeiro.
	return eventos.sort(
		(a, b) => dayjs(b.dataHora).valueOf() - dayjs(a.dataHora).valueOf()
	);
}

// Filtra incidentes pelo estado dos FiltrosDashboard.
// filtros: { inicio, fim, clientes: [], equipamentos: [], severidades: [], status: [] }
export function filtrarIncidentes(incidentes, filtros) {
	if (!filtros) return incidentes || [];
	return (incidentes || []).filter((incidente) => {
		const queda = dayjs(incidente.dataHoraQueda);
		if (filtros.inicio && queda.isBefore(dayjs(filtros.inicio))) return false;
		if (filtros.fim && queda.isAfter(dayjs(filtros.fim))) return false;
		if (
			filtros.clientes?.length &&
			!filtros.clientes.includes(incidente.alerta.clienteId)
		) {
			return false;
		}
		if (
			filtros.equipamentos?.length &&
			!filtros.equipamentos.includes(incidente.alerta.equipamentoId)
		) {
			return false;
		}
		if (
			filtros.severidades?.length &&
			!filtros.severidades.includes(incidente.severidade)
		) {
			return false;
		}
		if (filtros.status?.length && !filtros.status.includes(incidente.status)) {
			return false;
		}
		return true;
	});
}

// Filtra eventos globais pelo mesmo estado de filtros. Eventos de processo
// (atendimento/OS) sem severidade herdada permanecem visíveis quando há
// filtro de severidade — o filtro só corta severidades divergentes.
export function filtrarEventos(eventos, filtros) {
	if (!filtros) return eventos || [];
	return (eventos || []).filter((evento) => {
		const dataHora = dayjs(evento.dataHora);
		if (filtros.inicio && dataHora.isBefore(dayjs(filtros.inicio))) return false;
		if (filtros.fim && dataHora.isAfter(dayjs(filtros.fim))) return false;
		if (filtros.clientes?.length && !filtros.clientes.includes(evento.clienteId)) {
			return false;
		}
		if (
			filtros.equipamentos?.length &&
			!filtros.equipamentos.includes(evento.equipamentoId)
		) {
			return false;
		}
		if (
			filtros.severidades?.length &&
			evento.severidade &&
			!filtros.severidades.includes(evento.severidade)
		) {
			return false;
		}
		return true;
	});
}

function media(valores) {
	const validos = (valores || []).filter(
		(valor) => valor !== null && valor !== undefined && !Number.isNaN(valor)
	);
	if (!validos.length) return null;
	return validos.reduce((soma, valor) => soma + valor, 0) / validos.length;
}

// Agregados de KPI de um conjunto de incidentes (já filtrados por período).
export function resumirIncidentes(incidentes) {
	const lista = incidentes || [];
	const totalAlertas = lista.length;
	const alertasCriticos = lista.filter((i) => i.severidade === "critico").length;
	const semAtendimento = lista.filter((i) => !i.atendimento).length;
	const comAtendimento = lista.filter((i) => i.atendimento);
	const atendimentosSemOs = comAtendimento.filter((i) => !i.os).length;
	const comMttr = lista.filter((i) => i.mttr !== null);
	const slaCumpridos = comMttr.filter((i) => i.slaCumprido).length;

	return {
		totalAlertas,
		alertasCriticos,
		pctSemAtendimento: totalAlertas ? (semAtendimento / totalAlertas) * 100 : 0,
		pctAtendimentosSemOs: comAtendimento.length
			? (atendimentosSemOs / comAtendimento.length) * 100
			: 0,
		mttaMedio: media(lista.map((i) => i.mtta)),
		mttrMedio: media(lista.map((i) => i.mttr)),
		pctSlaCumprido: comMttr.length ? (slaCumpridos / comMttr.length) * 100 : null,
		incidentesAbertos: lista.filter((i) => i.aberto).length,
	};
}

// Volume diário de alertas empilhado por severidade, preenchendo dias vazios.
export function agruparAlertasPorDia(incidentes, inicio, fim) {
	const inicioDia = dayjs(inicio).startOf("day");
	const fimDia = dayjs(fim).startOf("day");
	const dias = [];
	for (
		let dia = inicioDia;
		!dia.isAfter(fimDia);
		dia = dia.add(1, "day")
	) {
		dias.push({
			data: dia.toISOString(),
			dia: dia.format("DD/MM"),
			critico: 0,
			alto: 0,
			medio: 0,
			baixo: 0,
			informativo: 0,
		});
	}
	const indicePorDia = {};
	dias.forEach((registro, indice) => {
		indicePorDia[dayjs(registro.data).format("YYYY-MM-DD")] = indice;
	});
	(incidentes || []).forEach((incidente) => {
		const chave = dayjs(incidente.dataHoraQueda).format("YYYY-MM-DD");
		const indice = indicePorDia[chave];
		if (indice !== undefined) dias[indice][incidente.severidade] += 1;
	});
	return dias;
}

// Top N equipamentos por volume de alertas no período.
export function topEquipamentos(incidentes, limite = 5) {
	const porEquipamento = {};
	(incidentes || []).forEach((incidente) => {
		const id = incidente.alerta.equipamentoId;
		if (!porEquipamento[id]) {
			porEquipamento[id] = {
				equipamento: incidente.equipamento,
				cliente: incidente.cliente,
				totalAlertas: 0,
				incidentesAbertos: 0,
			};
		}
		porEquipamento[id].totalAlertas += 1;
		if (incidente.aberto) porEquipamento[id].incidentesAbertos += 1;
	});
	return Object.values(porEquipamento)
		.sort((a, b) => b.totalAlertas - a.totalAlertas)
		.slice(0, limite);
}

// Resumo de SLA por cliente, ordenado do pior SLA para o melhor.
// A tendência compara o % de SLA cumprido com o período anterior.
export function slaPorCliente(incidentes, incidentesAnteriores, clientes) {
	const calcular = (lista) => {
		const porCliente = {};
		(lista || []).forEach((incidente) => {
			const id = incidente.alerta.clienteId;
			if (!porCliente[id]) {
				porCliente[id] = { total: 0, comMttr: 0, cumpridos: 0, mttrs: [] };
			}
			porCliente[id].total += 1;
			if (incidente.mttr !== null) {
				porCliente[id].comMttr += 1;
				porCliente[id].mttrs.push(incidente.mttr);
				if (incidente.slaCumprido) porCliente[id].cumpridos += 1;
			}
		});
		return porCliente;
	};

	const atual = calcular(incidentes);
	const anterior = calcular(incidentesAnteriores);

	return (clientes || [])
		.map((cliente) => {
			const dados = atual[cliente.id] || {
				total: 0,
				comMttr: 0,
				cumpridos: 0,
				mttrs: [],
			};
			const dadosAnteriores = anterior[cliente.id];
			const pctCumprido = dados.comMttr
				? (dados.cumpridos / dados.comMttr) * 100
				: null;
			const pctAnterior = dadosAnteriores?.comMttr
				? (dadosAnteriores.cumpridos / dadosAnteriores.comMttr) * 100
				: null;
			let tendencia = "estavel";
			if (pctCumprido !== null && pctAnterior !== null) {
				if (pctCumprido > pctAnterior) tendencia = "melhora";
				else if (pctCumprido < pctAnterior) tendencia = "piora";
			}
			return {
				cliente,
				contratoSla: cliente.contratoSla,
				totalIncidentes: dados.total,
				slaCumpridos: dados.cumpridos,
				slaEstourados: dados.comMttr - dados.cumpridos,
				pctCumprido,
				mttrMedio: media(dados.mttrs),
				tendencia,
			};
		})
		.filter((linha) => linha.totalIncidentes > 0)
		.sort((a, b) => {
			const pctA = a.pctCumprido === null ? 101 : a.pctCumprido;
			const pctB = b.pctCumprido === null ? 101 : b.pctCumprido;
			return pctA - pctB;
		});
}
